/**
 * A pedigree from a short text outline.
 *
 * Each line is one person, optionally with one partner, and the lines indented under it
 * are their children:
 *
 *   Walter (M, carrier, d. 1998) + Edith (F, carrier, b. 1925)
 *     Harold (M, carrier) + Joan (F, carrier)
 *       Peter (M, affected, proband)
 *
 * `+` joins partners, `=` marks a consanguineous union. In the brackets, words give the
 * person's sex, status and symbol: M, F or U (unknown); affected, carrier,
 * presymptomatic; deceased or `d. 2007`; proband, consultand, adopted; mz or dz for
 * twins; pregnancy, miscarriage, termination, stillbirth; separated on a partner; `b.
 * 1978`, `age 45` and "quoted notes" for the labels under the symbol.
 */

export type Sex = "m" | "f" | "u";
export type Status = "unaffected" | "affected" | "carrier" | "presymptomatic";
export type Kind =
  | "person"
  | "pregnancy"
  | "miscarriage"
  | "termination"
  | "stillbirth";
export type Relation = "mating" | "consanguineous" | "separated";

export interface PedigreePerson {
  name: string;
  sex: Sex;
  status: Status;
  kind: Kind;
  deceased: boolean;
  proband: boolean;
  consultand: boolean;
  adopted: boolean;
  twin: "mz" | "dz" | null;
  born: string | null;
  died: string | null;
  age: string | null;
  notes: string[];
}

export interface PedigreeUnit {
  person: PedigreePerson;
  partner: PedigreePerson | null;
  relation: Relation;
  children: PedigreeUnit[];
}

export interface UnknownWord {
  line: number;
  word: string;
}

export interface PedigreeParse {
  roots: PedigreeUnit[];
  /** Everyone in the outline, partners included. */
  count: number;
  unknown: UnknownWord[];
  /** The outline had more people than the drawing allows. */
  tooMany: boolean;
}

export const MAX_PEOPLE = 150;

const SEX_WORDS: Record<string, Sex> = {
  m: "m",
  male: "m",
  man: "m",
  boy: "m",
  f: "f",
  female: "f",
  woman: "f",
  girl: "f",
  u: "u",
  unknown: "u",
  unspecified: "u",
  "?": "u",
};
const STATUS_WORDS: Record<string, Status> = {
  affected: "affected",
  carrier: "carrier",
  presymptomatic: "presymptomatic",
  asymptomatic: "presymptomatic",
};
const KIND_WORDS: Record<string, Kind> = {
  pregnancy: "pregnancy",
  miscarriage: "miscarriage",
  sab: "miscarriage",
  termination: "termination",
  top: "termination",
  stillbirth: "stillbirth",
  sb: "stillbirth",
};
const DECEASED_WORDS = new Set(["deceased", "dead", "d"]);
const SEPARATED_WORDS = new Set(["separated", "divorced"]);

const BORN = /^b\.?\s*(\d.*)$/i;
const DIED = /^d\.?\s*(\d.*)$/i;
const AGE = /^age\s+(\d.*)$/i;
const AGE_YEARS = /^(\d{1,3})\s*y(?:rs?)?$/i;
const QUOTED = /^(["'])(.*)\1$/;
const PARTNER_SPLIT = /^(.*?)\s+([+=])\s+(.*)$/;
const PERSON = /^(.*?)\s*(?:\(([^)]*)\))?\s*$/;
const TABS = /\t/g;
const COMMENT = /^\s*#/;

function blankPerson(name: string): PedigreePerson {
  return {
    name,
    sex: "u",
    status: "unaffected",
    kind: "person",
    deceased: false,
    proband: false,
    consultand: false,
    adopted: false,
    twin: null,
    born: null,
    died: null,
    age: null,
    notes: [],
  };
}

/** Split a bracket's contents on commas that are not inside quotes. */
function splitAttributes(text: string): string[] {
  const parts: string[] = [];
  let current = "";
  let quote = "";
  for (const char of text) {
    if (quote) {
      current += char;
      if (char === quote) {
        quote = "";
      }
    } else if (char === '"' || char === "'") {
      quote = char;
      current += char;
    } else if (char === ",") {
      parts.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  parts.push(current);
  return parts.map((part) => part.trim()).filter(Boolean);
}

/** Apply one attribute to a person; false when the word means nothing. */
function applyAttribute(
  person: PedigreePerson,
  word: string,
  flags: { separated: boolean }
): boolean {
  const key = word.toLowerCase();
  const quoted = QUOTED.exec(word);
  if (quoted) {
    person.notes.push(quoted[2]);
    return true;
  }
  if (key in SEX_WORDS) {
    person.sex = SEX_WORDS[key];
  } else if (key in STATUS_WORDS) {
    person.status = STATUS_WORDS[key];
  } else if (key in KIND_WORDS) {
    person.kind = KIND_WORDS[key];
  } else if (DECEASED_WORDS.has(key)) {
    person.deceased = true;
  } else if (SEPARATED_WORDS.has(key)) {
    flags.separated = true;
  } else if (key === "proband") {
    person.proband = true;
  } else if (key === "consultand") {
    person.consultand = true;
  } else if (key === "adopted") {
    person.adopted = true;
  } else if (key === "mz" || key === "dz") {
    person.twin = key;
  } else {
    return applyLabel(person, word);
  }
  return true;
}

/** The birth, death and age labels. */
function applyLabel(person: PedigreePerson, word: string): boolean {
  const born = BORN.exec(word);
  const died = DIED.exec(word);
  const age = AGE.exec(word) ?? AGE_YEARS.exec(word);
  if (born) {
    person.born = born[1];
  } else if (died) {
    person.deceased = true;
    person.died = died[1];
  } else if (age) {
    person.age = age[1];
  } else {
    return false;
  }
  return true;
}

function readPerson(
  text: string,
  line: number,
  unknown: UnknownWord[]
): { person: PedigreePerson; separated: boolean } {
  const match = PERSON.exec(text.trim());
  const person = blankPerson((match?.[1] ?? "").trim());
  const flags = { separated: false };
  for (const word of splitAttributes(match?.[2] ?? "")) {
    if (!applyAttribute(person, word, flags)) {
      unknown.push({ line, word });
    }
  }
  return { person, separated: flags.separated };
}

function indentOf(raw: string): number {
  const expanded = raw.replace(TABS, "    ");
  return expanded.length - expanded.trimStart().length;
}

/** One outline line as a unit: a person, an optional partner, and how they are joined. */
function readUnit(
  raw: string,
  line: number,
  unknown: UnknownWord[]
): PedigreeUnit {
  const split = PARTNER_SPLIT.exec(raw.trim());
  const first = readPerson(split ? split[1] : raw, line, unknown);
  const second = split ? readPerson(split[3], line, unknown) : null;
  let relation: Relation = "mating";
  if (split?.[2] === "=") {
    relation = "consanguineous";
  }
  if (second?.separated || first.separated) {
    relation = "separated";
  }
  return {
    person: first.person,
    partner: second?.person ?? null,
    relation,
    children: [],
  };
}

interface Open {
  indent: number;
  unit: PedigreeUnit;
}

/** Hang a unit under the nearest open line that is indented less, or make it a root. */
function attach(
  stack: Open[],
  roots: PedigreeUnit[],
  unit: PedigreeUnit,
  indent: number
): void {
  while (stack.length > 0 && (stack.at(-1)?.indent ?? 0) >= indent) {
    stack.pop();
  }
  const parent = stack.at(-1);
  if (parent) {
    parent.unit.children.push(unit);
  } else {
    roots.push(unit);
  }
  stack.push({ indent, unit });
}

export function parsePedigree(text: string): PedigreeParse {
  const roots: PedigreeUnit[] = [];
  const unknown: UnknownWord[] = [];
  const stack: Open[] = [];
  let count = 0;
  for (const [index, raw] of text.split("\n").entries()) {
    if (raw.trim() === "" || COMMENT.test(raw)) {
      continue;
    }
    const unit = readUnit(raw, index + 1, unknown);
    count += unit.partner ? 2 : 1;
    attach(stack, roots, unit, indentOf(raw));
  }
  return { roots, count, unknown, tooMany: count > MAX_PEOPLE };
}

const ROMAN: [number, string][] = [
  [1000, "M"],
  [900, "CM"],
  [500, "D"],
  [400, "CD"],
  [100, "C"],
  [90, "XC"],
  [50, "L"],
  [40, "XL"],
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
];

/** 1 → I, 4 → IV, 9 → IX: the generation numbers of a pedigree. */
export function toRoman(value: number): string {
  let rest = Math.max(0, Math.floor(value));
  let out = "";
  for (const [size, letters] of ROMAN) {
    while (rest >= size) {
      out += letters;
      rest -= size;
    }
  }
  return out;
}
